import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  fetchAccountCapabilities,
  type AccountCapabilities,
} from '@/services/accountCapabilitiesService'

export const useAccountCapabilitiesStore = defineStore('accountCapabilities', () => {
  const pricingHubEnabled = ref(false)
  const pricingHubUrl = ref<string | undefined>(undefined)
  const hasLoaded = ref(false)
  const loadError = ref(false)

  let loadSeq = 0

  const pricingHubCapabilitiesLoaded = computed(() => hasLoaded.value)

  const reset = () => {
    loadSeq += 1
    pricingHubEnabled.value = false
    pricingHubUrl.value = undefined
    hasLoaded.value = false
    loadError.value = false
  }

  const applyCapabilities = (capabilities: AccountCapabilities) => {
    pricingHubEnabled.value = capabilities.pricingHubEnabled === true
    pricingHubUrl.value =
      typeof capabilities.pricingHubUrl === 'string' && capabilities.pricingHubUrl.trim()
        ? capabilities.pricingHubUrl.trim()
        : undefined
    hasLoaded.value = true
  }

  const loadForAccount = async (endpoint: string, jazelId: string, token: string) => {
    const seq = ++loadSeq
    const accountId = String(jazelId || '').trim()
    const sessionToken = String(token || '').trim()

    if (!accountId || !sessionToken) {
      reset()
      return
    }

    hasLoaded.value = false
    loadError.value = false

    try {
      const result = await fetchAccountCapabilities(endpoint, accountId, { token: sessionToken })

      if (seq !== loadSeq) return

      applyCapabilities(result.capabilities)
      loadError.value = result.loadError
    } catch {
      if (seq !== loadSeq) return

      pricingHubEnabled.value = false
      pricingHubUrl.value = undefined
      loadError.value = true
      hasLoaded.value = true
    }
  }

  return {
    pricingHubEnabled,
    pricingHubUrl,
    hasLoaded,
    loadError,
    pricingHubCapabilitiesLoaded,
    reset,
    applyCapabilities,
    loadForAccount,
  }
})
